/**
 * Client de l'API côté enseignant.
 *
 * Le PIN part dans un en-tête, jamais dans l'URL. Il n'est gardé qu'en mémoire,
 * le temps de la séance : rien n'est écrit sur la machine de l'enseignant.
 */
export const EN_TETE_PIN = 'x-neopolis-pin';

export interface ProgressionIlot {
  readonly islandCode: string;
  readonly chapterId: string | null;
  readonly stepIndex: number | null;
  readonly gauges: Record<string, number> | null;
  readonly solutionDeployee: string | null;
  readonly derniereActivite: string | null;
  readonly tempsPasseSecondes: number | null;
}

export interface SuiviDeClasse {
  readonly classCode: string;
  readonly chapitres: readonly string[];
  readonly ilots: readonly ProgressionIlot[];
}

export interface ClasseCreee {
  readonly classCode: string;
  readonly nom: string;
  readonly ilots: readonly string[];
}

export interface NiveauxQuestionnaire {
  readonly peu: number;
  readonly moyen: number;
  readonly bien: number;
}

export interface ReponsesQuestionnaire {
  readonly chapitre: string;
  readonly compris: NiveauxQuestionnaire;
  readonly aime: NiveauxQuestionnaire;
  readonly difficultes: readonly string[];
}

export interface AnalyseDEtape {
  readonly chapitre: string;
  readonly etape: number;
  readonly titre: string;
  readonly tempsMedianSecondes: number | null;
  readonly ilotsArrives: number;
  readonly ilotsTermines: number;
  readonly ilotsBloques: number;
}

export interface ErreurFrequente {
  readonly type: string;
  readonly detail: string;
  readonly libelle: string;
  readonly occurrences: number;
  readonly ilots: number;
}

export interface AnalyseDeClasse {
  readonly classCode: string;
  readonly seance: {
    readonly ilots: number;
    readonly etapes: readonly AnalyseDEtape[];
    readonly blocages: readonly AnalyseDEtape[];
    readonly erreurs: readonly ErreurFrequente[];
  };
  readonly questionnaires: readonly ReponsesQuestionnaire[];
}

export interface ClientProf {
  creerClasse(nom: string, pin: string, ilots: number): Promise<ClasseCreee | null>;
  suivre(classCode: string, pin: string): Promise<SuiviDeClasse | null>;
  definirChapitres(
    classCode: string,
    pin: string,
    chapitres: readonly string[],
  ): Promise<readonly string[] | null>;
  analyser(classCode: string, pin: string): Promise<AnalyseDeClasse | null>;
  /** Efface la classe et tout ce qui s'y rattache. */
  supprimerClasse(classCode: string, pin: string): Promise<boolean>;
  /** Efface la partie d'un îlot ; son code reste valable. */
  reinitialiserIlot(classCode: string, pin: string, islandCode: string): Promise<boolean>;
}

export function createClientProf(base = ''): ClientProf {
  const url = (chemin: string): string => `${base}${chemin}`;

  return {
    async creerClasse(nom, pin, ilots) {
      try {
        const reponse = await fetch(url('/api/classes'), {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ nom, pin, ilots }),
        });
        return reponse.ok ? ((await reponse.json()) as ClasseCreee) : null;
      } catch {
        return null;
      }
    },

    async suivre(classCode, pin) {
      try {
        const reponse = await fetch(url(`/api/classes/${classCode}/progress`), {
          headers: { [EN_TETE_PIN]: pin },
        });
        return reponse.ok ? ((await reponse.json()) as SuiviDeClasse) : null;
      } catch {
        return null;
      }
    },

    async analyser(classCode, pin) {
      try {
        const reponse = await fetch(url(`/api/classes/${classCode}/analyse`), {
          headers: { [EN_TETE_PIN]: pin },
        });
        return reponse.ok ? ((await reponse.json()) as AnalyseDeClasse) : null;
      } catch {
        return null;
      }
    },

    async definirChapitres(classCode, pin, chapitres) {
      try {
        const reponse = await fetch(url(`/api/classes/${classCode}/chapters`), {
          method: 'PATCH',
          headers: { 'content-type': 'application/json', [EN_TETE_PIN]: pin },
          body: JSON.stringify({ chapitres }),
        });
        if (!reponse.ok) {
          return null;
        }
        const corps = (await reponse.json()) as { chapitres: string[] };
        return corps.chapitres;
      } catch {
        return null;
      }
    },

    async supprimerClasse(classCode, pin) {
      try {
        const reponse = await fetch(url(`/api/classes/${classCode}`), {
          method: 'DELETE',
          headers: { [EN_TETE_PIN]: pin },
        });
        return reponse.ok;
      } catch {
        return false;
      }
    },

    async reinitialiserIlot(classCode, pin, islandCode) {
      try {
        const reponse = await fetch(
          url(`/api/classes/${classCode}/ilots/${islandCode}/sauvegarde`),
          {
            method: 'DELETE',
            headers: { [EN_TETE_PIN]: pin },
          },
        );
        return reponse.ok;
      } catch {
        return false;
      }
    },
  };
}
